/**
 * Confirmación de "agregado al carrito" como toast no bloqueante.
 *
 * Antes era un Dialog modal con timer de 20s: cada alta al carrito frenaba la
 * compra y exigía un clic extra para seguir navegando (SCRUM-343). Ahora se
 * muestra un toast que se cierra solo y deja el CTA de compra a mano, sin
 * capturar el foco ni bloquear la página.
 *
 * El bus (`market-added-to-cart.ts`) no cambió: los ~8 call sites del storefront
 * siguen llamando `showAddedToCart()` igual que antes.
 */
import { useEffect, useSyncExternalStore } from "react";
import { Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import {
  ADDED_TO_CART_DURATION_MS,
  dismissAddedToCart,
  getAddedToCartGeneration,
  getAddedToCartSnapshot,
  runAddedToCartBuy,
  subscribeAddedToCart,
  type AddedToCartPayload,
} from "@/lib/market-added-to-cart";
import { storefrontPath } from "@/lib/tenant-host-paths";

function useAddedToCart(): { payload: AddedToCartPayload | null; generation: number } {
  const payload = useSyncExternalStore(
    subscribeAddedToCart,
    getAddedToCartSnapshot,
    () => null,
  );
  const generation = useSyncExternalStore(
    subscribeAddedToCart,
    getAddedToCartGeneration,
    () => 0,
  );
  return { payload, generation };
}

function goToCheckout(payload: AddedToCartPayload) {
  if (runAddedToCartBuy()) return;
  const href =
    typeof payload.checkoutHref === "string" && payload.checkoutHref.trim()
      ? payload.checkoutHref.trim()
      : storefrontPath(payload.storeSlug, "/checkout");
  dismissAddedToCart();
  window.location.href = href;
}

export function MarketAddedToCartToaster() {
  const { payload, generation } = useAddedToCart();

  useEffect(() => {
    if (!payload) return;
    const showBuy = payload.checkoutHref !== false;

    const id = toast.custom(
      () => (
        <div
          data-testid="market-added-to-cart-toast"
          className="flex w-full items-center gap-3 rounded-xl border border-emerald-200/80 bg-[var(--market-surface,#fff)] px-4 py-3 shadow-[0_12px_32px_rgba(0,0,0,0.14)]"
        >
          {payload.imageUrl ? (
            <img
              src={payload.imageUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-black/5"
            />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check className="h-5 w-5" strokeWidth={2.5} aria-hidden />
            </span>
          )}

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--market-on-surface,#0f172a)]">
              Agregado al carrito
            </p>
            <p className="truncate text-xs text-[var(--market-on-surface-variant,#64748b)]">
              {payload.title}
              {payload.qty && payload.qty > 1 ? ` · ${payload.qty}u` : ""}
            </p>
          </div>

          {showBuy ? (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#0f172a] px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
              onClick={() => goToCheckout(payload)}
            >
              <ShoppingBag className="h-3.5 w-3.5" aria-hidden />
              Ver carrito
            </button>
          ) : null}
        </div>
      ),
      {
        duration: ADDED_TO_CART_DURATION_MS,
        // El bus queda limpio aunque el toast se cierre solo o lo cierre el usuario.
        onAutoClose: dismissAddedToCart,
        onDismiss: dismissAddedToCart,
      },
    );

    return () => {
      toast.dismiss(id);
    };
  }, [payload, generation]);

  return null;
}
